import { supabase } from '@/lib/supabase';

// Flag to control whether data is saved to Supabase
// Set to false to disable Supabase saving completely
export const ENABLE_SUPABASE_SAVING = true;

// Helper to normalize text by removing accents/diacritics
const normalizeText = (text: string): string => {
  if (!text) return '';
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

// Ensure code follows the LHP-XXXXXX format (6 characters, not 4)
const formatConfirmationCode = (code: string): string => {
  if (!code) return '';
  
  const normalizedCode = normalizeText(code.toUpperCase());
  
  // If already has prefix but wrong format
  if (normalizedCode.startsWith('LHP-')) {
    // Extract the code part after the prefix
    const codePart = normalizedCode.substring(4);
    // Make sure it's at least 6 characters long
    return `LHP-${codePart.replace(/[^A-Z0-9]/g, '').padStart(6, '0')}`;
  }
  
  // If it's just the code part without prefix
  if (/^[A-Z0-9]+$/.test(normalizedCode)) {
    // Make sure it's at least 6 characters long
    return `LHP-${normalizedCode.padStart(6, '0')}`;
  }
  
  // Fallback: normalize and add prefix
  const cleanCode = normalizedCode.replace(/[^A-Z0-9]/g, '').padStart(6, '0');
  return `LHP-${cleanCode}`;
};

export const saveToSupabase = async (attendeeData: any) => {
  if (!attendeeData || !ENABLE_SUPABASE_SAVING) return null;
  
  try {
    // Create a copy of the data to avoid modifying the original
    const processedData = { ...attendeeData };
    
    // Format the confirmation code properly
    processedData.code = formatConfirmationCode(processedData.code || '');
    
    // Fix t-shirt size data if it's not properly formatted
    if (!processedData.t_shirt_size || processedData.t_shirt_size === '') {
      if (processedData.gender) {
        processedData.t_shirt_size = processedData.gender === 'female' ? 'Nu_L' : 'Nam_L';
      } else {
        // Ultimate fallback if no gender info
        processedData.t_shirt_size = '';
      }
    } 
    // Ensure the prefix exists if needed
    else if (!processedData.t_shirt_size.startsWith('Nam_') && !processedData.t_shirt_size.startsWith('Nu_')) {
      const prefix = processedData.gender === 'female' ? 'Nu_' : 'Nam_';
      processedData.t_shirt_size = `${prefix}${processedData.t_shirt_size}`;
    }
    
    // Map the data to match the Supabase schema structure
    const supabaseData = {
      name: processedData.name,
      email: processedData.email,
      phone: processedData.phone,
      class: processedData.class,
      contribution: processedData.contribution,
      code: processedData.code,
      attended: false,
      "registered_at": new Date().toISOString(),
      "t_shirt_size": processedData.t_shirt_size
    };
    
    const { data, error } = await supabase
      .from('attendees')
      .insert([supabaseData]);
      
    if (error) {
      return { success: false, error };
    } else {
      return { success: true, data };
    }
  } catch (error) {
    return { success: false, error };
  }
};

export const saveToLocalStorage = (data: any) => {
  try {
    if (!data) return;
    
    // Format the confirmation code
    const processedData = {
      ...data,
      code: formatConfirmationCode(data.code || '')
    };
    
    const storedAttendees = localStorage.getItem('attendees') || '[]';
    const attendees = JSON.parse(storedAttendees);
    localStorage.setItem('attendees', JSON.stringify([...attendees, processedData]));
    return true;
  } catch (err) {
    return false;
  }
};
